import { defineStore } from 'pinia';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes en millisecondes
const STORAGE_KEY = 'community_detail_cache';

export const useCommunityDetailStore = defineStore('communityDetail', {
  state: () => ({
    cache: {}, // { [did]: { data: {...}, lastFetchTime: number } }
  }),

  getters: {
    isCacheValid: (state) => (did) => {
      const entry = state.cache[did];
      if (!entry || !entry.data) return false;
      return Date.now() - entry.lastFetchTime < CACHE_DURATION;
    },

    getCommunityData: (state) => (did) => {
      const entry = state.cache[did];
      return entry?.data || null;
    },
  },

  actions: {
    loadFromLocalStorage() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          this.cache = parsed;
        }
      } catch (e) {
        console.warn('Failed to load community detail cache:', e);
      }
    },

    saveToLocalStorage() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.cache));
      } catch (e) {
        console.warn('Failed to save community detail cache:', e);
      }
    },

    setCache(did, data) {
      this.cache[did] = {
        data: { ...data },
        lastFetchTime: Date.now(),
      };
      this.saveToLocalStorage();
    },

    async fetchCommunity(did, api, utils, forceRefresh = false) {
      if (!did) return null;

      if (this.isCacheValid(did) && !forceRefresh) {
        return this.getCommunityData(did);
      }

      const addr = utils.didToAddress(did);
      const result = {
        profile: null,
        socialMedia: [],
        templateLists: null,
        memberCount: 0,
        lastTemplates: null,
        membershipTemplate: null,
        invitationLink: '',
        createdAt: null,
      };

      let loading = 0;

      const savePartial = () => {
        this.setCache(did, result);
      };

      try {
        // 1. Fetch profile from issuer details
        const issuerRes = await api.getIssuerDetailsLinkFromCode(did);
        const profileIpfsUrl = issuerRes.data.service[0]?.serviceEndpoint;

        if (profileIpfsUrl) {
          result.profile = await utils.getJsonDataFromUrl(profileIpfsUrl);
          const socialNetworks = ['twitter', 'discord', 'website'];
          result.socialMedia = socialNetworks
            .filter((network) => result.profile[network])
            .map((network) => ({
              name: network,
              value: result.profile[network],
            }));
          savePartial();
        }

        // 2. Fetch template lists
        const typeList = await Promise.all(
          ['Basic', 'Community', 'Participation', 'Membership', 'Role'].map(
            async (type) => {
              const list = await utils.getTemplateList(addr, type);
              return { type, list };
            }
          )
        );

        const lists = {};
        typeList.forEach((el) => (lists[el.type] = el.list));
        result.templateLists = lists;
        const membershipFirst = lists['Membership']?.[0];
        const memberShipTemplateHash = membershipFirst?.templateHash;
        result.createdAt = membershipFirst?.status?.timestamp
          ? Number(membershipFirst.status.timestamp)
          : null;
        savePartial();

        if (memberShipTemplateHash) {
          // 3. Fetch membership template data
          result.membershipTemplate = await utils.getTemplateData(
            memberShipTemplateHash
          );
          savePartial();

          // 4. Fetch last templates
          const nonMembershipLists = Object.values(lists).filter(
            (list) => list !== lists['Membership']
          );
          const templateItems = nonMembershipLists
            .flatMap((list) => list)
            .slice(0, 5)
            .sort((a, b) => (b.status?.timestamp || 0) - (a.status?.timestamp || 0));

          result.lastTemplates = await utils.getTemplateListData(templateItems);
          savePartial();

          // 5. Fetch member count
          try {
            const countRes = await api.getCommunityMemberCount(
              memberShipTemplateHash.split('0x')[1]
            );
            result.memberCount = countRes.data?.members?.number ?? 0;
          } catch (e) {
            result.memberCount = 0;
          }
          savePartial();
        }

        // 6. Fetch invitation link
        try {
          const memberCardList = await utils.getMemberCardsList();
          const memberCard = memberCardList.find((card) => card.issuer === addr);
          if (memberCard) {
            const invitationLinkCode = utils.removeLeadingZerosAndConvertToASCII(
              memberCard.invitationLink
            );
            result.invitationLink = `${process.env.VUE_APP_REDIRECT_LINK}/${invitationLinkCode}`;
          }
        } catch (e) {
          // ignore
        }
        savePartial();

        return result;
      } catch (error) {
        console.error('Error fetching community:', error);
        throw error;
      }
    },

    clearCache(did) {
      if (did) {
        delete this.cache[did];
      } else {
        this.cache = {};
      }
      this.saveToLocalStorage();
    },
  },
});
